// M132 Fase A — Live Site Auditor (skeleton).
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useEmbed } from "@/contexts/EmbedContext";
import api from "@/lib/api";
import { ScanLine, Loader2, AlertTriangle, Sparkles } from "lucide-react";

const SEV_COLOR = {
  hoog: "text-neg-red border-neg-red/40 bg-neg-red/5",
  middel: "text-warn-amber border-warn-amber/40 bg-warn-amber/5",
  laag: "text-text-muted border-border-hairline bg-bg-panel",
};

export default function SiteAuditPage() {
  const { hideInnerHeader } = useEmbed();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);
  const [solutions, setSolutions] = useState({});
  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/site-audit/overview");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Site Audit niet beschikbaar.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const runScan = async () => {
    setBusy(true); setErr(null);
    try {
      const { data: r } = await api.post("/site-audit/scan", { site_url: url || undefined });
      // Reload overview after scan
      const { data } = await api.get("/site-audit/overview");
      setData(data);
      window.alert(`Scan klaar: ${r.pages_scanned} pagina('s), ${r.findings_total} bevindingen (${r.findings_new} nieuw).`);
    } catch (e) {
      setErr(e?.response?.data?.detail?.reason_nl || e?.response?.data?.detail || "Scan mislukt.");
    } finally { setBusy(false); }
  };

  const genSolution = async (card) => {
    setActive({ id: card.identity_key, busy: "gen" });
    try {
      const { data } = await api.post("/site-audit/findings/generate-solution", { finding_id: card.identity_key });
      setSolutions((s) => ({ ...s, [card.identity_key]: data.proposed_solution }));
    } catch (e) {
      window.alert(e?.response?.data?.detail?.reason_nl || "Genereren mislukt.");
    } finally { setActive(null); }
  };

  const queueSolution = async (card) => {
    setActive({ id: card.identity_key, busy: "queue" });
    try {
      const { data } = await api.post("/site-audit/findings/queue", { finding_id: card.identity_key });
      setSolutions((s) => ({ ...s, [card.identity_key]: { ...(s[card.identity_key] || {}), queued_id: data.proposal_id } }));
      window.alert(data.toast_nl || "Klaargezet in Publish Queue.");
    } catch (e) {
      window.alert(e?.response?.data?.detail?.reason_nl || "Klaarzetten mislukt.");
    } finally { setActive(null); }
  };

  const cards = Array.isArray(data?.cards) ? data.cards : [];
  return (
    <AppLayout>
      {!hideInnerHeader && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-text-primary tracking-tight flex items-center gap-2" data-testid="audit-page-title">
            <ScanLine className="w-5 h-5 text-accent-teal" /> Site-analyse
          </h1>
          <p className="text-sm text-text-muted mt-1">
            Live inspectie met concrete verbetervoorstellen — veilig-modus.
          </p>
        </div>
      )}

      <section className="rounded-card border border-border-hairline bg-bg-panel-raised p-4 mb-6" data-testid="audit-scan-form">
        <div className="text-[10px] text-text-muted mb-2">Website scannen</div>
        <div className="flex flex-col md:flex-row gap-2">
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://voorbeeld.nl"
                  data-testid="audit-url-input"
                  className="flex-1 text-sm bg-bg-panel border border-border-hairline rounded px-2 py-1.5 text-text-primary" />
          <button onClick={runScan} data-testid="audit-scan-btn"
                   disabled={!data?.scan_available || busy}
                   className="text-[11px] px-3 py-1.5 rounded border border-accent-teal/40 hover:bg-accent-teal/10 text-accent-teal disabled:opacity-50 flex items-center gap-1">
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}
            {data?.scan_available ? "Scan starten" : "Scan starten (volgt)"}
          </button>
        </div>
        {data?.site_url_hint && !url && (
          <div className="mt-2 text-[10px] text-text-subtle">Suggestie: {data.site_url_hint}</div>
        )}
        {data?.last_scan_at && (
          <div className="mt-1 text-[10px] text-text-subtle">Laatste scan: {data.last_scan_at} ({data.last_scan_pages} pagina's)</div>
        )}
        {!data?.scan_available && (
          <div className="mt-2 text-[11px] text-text-subtle italic">
            De echte scanner staat klaar en wordt in de volgende fase geactiveerd. Structuur en oplossings-flow zie je hieronder alvast.
          </div>
        )}
      </section>

      {err && <div className="mb-4 text-xs text-warn-amber italic">{err}</div>}

      {!data ? (
        <div className="text-text-muted text-sm flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Auditor laden…</div>
      ) : (
        <section data-testid="audit-cards">
          <h2 className="text-[11px] text-text-muted font-semibold mb-3">Audit-bevindingen</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cards.map((c) => {
              const sev = SEV_COLOR[c.severity] || SEV_COLOR.laag;
              return (
                <div key={c.identity_key} data-testid={`audit-card-${c.rule}`}
                      className={`rounded-md border p-4 ${c.is_placeholder ? "border-warn-amber/40 border-dashed bg-warn-amber/5" : "border-border-hairline bg-bg-panel"}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded border ${sev}`}>
                      {c.severity || "onbekend"}
                    </span>
                    {c.is_placeholder && (
                      <span className="text-[9px] text-warn-amber font-semibold">Voorbeeldweergave</span>
                    )}
                  </div>
                  <div className="text-sm font-semibold text-text-primary mb-1">{c.title}</div>
                  <div className="text-[11px] text-text-muted mb-2">{c.page_hint}</div>
                  <div className="text-xs text-text-muted leading-relaxed">{c.detail_nl || c.body_nl}</div>
                  {(solutions[c.identity_key] || c.proposed_solution) && (
                    <div className="mt-2 mb-2 rounded border border-accent-teal/30 bg-accent-teal/5 p-2" data-testid={`audit-solution-${c.rule}`}>
                      <div className="text-[9px] text-accent-teal font-semibold mb-1">Concrete oplossing</div>
                      <pre className="text-[10px] text-text-primary whitespace-pre-wrap max-h-32 overflow-auto">{JSON.stringify(((solutions[c.identity_key] || c.proposed_solution) || {}).payload || {}, null, 2)}</pre>
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {!c.is_placeholder && !solutions[c.identity_key]?.queued_id && !c.queued_proposal_id && (
                      <>
                        {!(solutions[c.identity_key] || c.proposed_solution) ? (
                          <button data-testid={`audit-generate-${c.rule}`} onClick={() => genSolution(c)}
                                   disabled={active?.id === c.identity_key}
                                   className="text-[11px] px-2 py-1 rounded border border-accent-teal/40 hover:bg-accent-teal/10 text-accent-teal flex items-center gap-1 disabled:opacity-50">
                            {active?.id === c.identity_key && active.busy === "gen" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                            Genereer oplossing
                          </button>
                        ) : (
                          <button data-testid={`audit-queue-${c.rule}`} onClick={() => queueSolution(c)}
                                   disabled={active?.id === c.identity_key}
                                   className="text-[11px] px-2 py-1 rounded border border-pos-green/40 hover:bg-pos-green/10 text-pos-green flex items-center gap-1 disabled:opacity-50">
                            {active?.id === c.identity_key && active.busy === "queue" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                            Zet klaar voor uitvoering
                          </button>
                        )}
                      </>
                    )}
                    {c.is_placeholder && (
                      <button data-testid={`audit-generate-${c.rule}`} disabled
                               className="text-[11px] px-2 py-1 rounded border border-accent-teal/40 text-accent-teal opacity-50 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Genereer oplossing
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {data.note_nl && <p className="mt-3 text-[11px] text-text-subtle italic">{data.note_nl}</p>}
        </section>
      )}
    </AppLayout>
  );
}
